import { SearchParams } from '@/types'
import {
  TelemetryWeatherReportParams,
  WeatherReportParams,
} from '@/types/weather'
import { queryStringify } from '@/ultils'
import axiosClient, { createAxiosInstance } from '../axiosClient'
import { TELEMETRY_API, TELEMETRY_API_KEY } from '@/constant/constants'

const WeatherApi = {
  deleteSoilMoisture(
    interfaceId: number,
    day: string,
    sensorId: number,
    stationId: number | undefined,
    verificationToken: string,
  ) {
    const url = `/api/sensor/delete-data-soilmoisture/?interfaceId=${interfaceId}&sensorId=${sensorId}&stationId=${stationId}&day=${day}`
    return axiosClient.delete(url, {
      headers: { 'X-Step-Up-Verification': verificationToken },
    })
  },
  getWeather(lat: number, lon: number) {
    const url = `/api/weather?lat=${lat}&lon=${lon}`
    return axiosClient.get(url)
  },
  getWeatherForecast(lat: number, lon: number) {
    const url = `/api/weather/forecast?lat=${lat}&lon=${lon}`
    return axiosClient.get(url)
  },

  getWeatherHumidity(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/humidity`
    return axiosClient.get(
      url + queryStringify({ ...searchParams, stationId: stationId }),
    )
  },
  getWeatherET(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/et`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherRain(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/rain`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherTotalRain(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/rain-total`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherBarometer(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/barometers`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherBarometerHistory(
    interfaceId: number,
    stationId: number,
    from: string,
    to: string,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/barometers`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
          ...(!!from && { from: from }),
          ...(!!to && { to: to }),
        }),
    )
  },
  getWeatherWindSpeed(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/wind-speed`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherSolar(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/solar`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherTHSW(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/thsw-index`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherTemperature(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/temperature`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherWindRose(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `/api/weather/weatherlink/${interfaceId}/wind-rose`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },
  getWeatherWindDirection(
    interfaceId: number,
    stationId: number,
    searchParams: SearchParams,
  ) {
    const url = `api/weather/weatherlink/${interfaceId}/wind-direction`
    return axiosClient.get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!stationId && { stationId: stationId }),
        }),
    )
  },

  getLastUpdated(stationId: number) {
    const url = `api/weather/weatherlink/${stationId}/get-last-updated`
    return axiosClient.post(url)
  },

  exportCSV(
    interfaceId: number,
    data: {
      fromDate: string
      toDate: string
      stationWeatherLinkIds: number[]
    },
  ) {
    const url = `/api/weather/export-weatherlink`
    return axiosClient.post(url, data, {
      params: { interfaceId },
      responseType: 'blob',
    })
  },

  //Data comparison
  getWeatherDataComparison(
    interfaceId: number,
    stationId: number,
    data: {
      fromDate: string
      endDate: string
      isAvg: boolean
    },
  ) {
    const url = `/api/sensor/weather-data`
    return axiosClient.post(url, data, {
      params: {
        interfaceId,
        stationId,
      },
    })
  },

  getComparisonData(
    interfaceId: number,
    data: {
      fromDate: string
      endDate: string
      isAvg: boolean
      listSensorIds: number[]
    },
  ) {
    const url = `/api/sensor/comparison-data`

    return axiosClient.get(url + queryStringify({ ...data, interfaceId }))
  },

  getComparisonSensor(interfaceId: number) {
    const url = `/api/sensor/comparison-sensor`
    return axiosClient.get(url, {
      params: {
        interfaceId,
      },
    })
  },
  getWeatherReport(params: WeatherReportParams) {
    const url = `/api/weather/report/historic`
    return axiosClient.get(
      url +
        queryStringify({
          ...params,
        }),
    )
  },
  getAssetList(EntityId: number, searchParams: SearchParams) {
    const url = `/api/Asset/list`
    return createAxiosInstance({
      baseURL: TELEMETRY_API,
      apiKey: TELEMETRY_API_KEY,
    }).get(
      url +
        queryStringify({
          ...{ EntityId: EntityId },
          ...searchParams,
        }),
    )
  },
  getTagKey(AssetId: number) {
    const url = `/api/Tag/keys/` + AssetId
    return createAxiosInstance({
      baseURL: TELEMETRY_API,
      apiKey: TELEMETRY_API_KEY,
    }).get(url)
  },
  getWeatherReportV2(params: TelemetryWeatherReportParams) {
    const url = `/api/Tag/list`
    return createAxiosInstance({
      baseURL: TELEMETRY_API,
      apiKey: TELEMETRY_API_KEY,
    }).get(
      url +
        queryStringify({
          ...params,
        }),
    )
  },
  getWeatherBarometerV2(AssetId: number, searchParams: SearchParams) {
    const url = `/api/Tag/list`
    return createAxiosInstance({
      baseURL: TELEMETRY_API,
      apiKey: TELEMETRY_API_KEY,
    }).get(
      url +
        queryStringify({
          ...searchParams,
          ...(!!AssetId && { AssetId: AssetId }),
        }),
    )
  },
}

export default WeatherApi
